import { isUndefined } from 'lodash'
import last from 'lodash/last'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'

import { AI_MESSAGE_ATTRIBUTE } from '@shared/constants/ai-messages'
import EventBus, { GlobalEvents } from '@shared/event-bus'
import { AiTypingStatus } from '@shared/models/enums/typing-indicator'
import { MessageCreateData } from '@shared/models/interfaces/messages'
import { IMessage } from '@shared/models/proto/base/messages'
import { Optional } from '@shared/models/types/primary'
import { useTypingIndicatorsStore } from '@shared/store/typing-indicators'
import { isMessageSendToAi } from '@shared/utils/helpers/chat/ai-agent/is-message-to-ai'
import {
  isNetworkError,
  isServerError,
} from '@shared/utils/helpers/error-guards/common-errors'
import { isGptBotPayload } from '@shared/utils/helpers/messages/bot-payload'
import { isPending } from '@shared/utils/helpers/request-status'

import { MessageType } from '@protos/chat/message_type_pb'

import { STICKY_AI_QUESTION_HEIGHT } from '@pc/constants/elements-height'
import { useConversationsStore } from '@pc/store/conversations'
import { useMessagesStore } from '@pc/store/messages'

/**
 * diagram - https://gitlab.com/qliqdev/qliqweb-vue/-/blob/dev/docs/marmaid/pc/use-chat-messages.md?ref_type=heads
 * */

export const useChatMessages = () => {
  const messagesStore = useMessagesStore()

  const stickyLastQuestionHeight = ref(0)
  const messages = computed(() => messagesStore.messages)
  const sendMessageState = computed(() => messagesStore.sendMessageState)

  const conversationsStore = useConversationsStore()
  const { currentConversation: conversation } = storeToRefs(conversationsStore)

  const isLoading = computed(() => isPending(sendMessageState.value))

  const serverError = computed(() =>
    isServerError(sendMessageState?.value?.status?.error as Error)
  )
  const networkError = computed(() =>
    isNetworkError(sendMessageState?.value?.status?.error as Error)
  )
  const errorMessage = computed(() => networkError.value || serverError.value)

  const messagesList = computed(() =>
    Array.from(messages.value?.data?.data ?? []).reverse()
  )

  const isLastMessage = (message: IMessage) =>
    message === last(messagesList.value)

  const isAnimatedMessage = (message?: IMessage) =>
    message && messagesStore.animateMessage === message.id

  const lastMessage = computed<Optional<IMessage>>(() =>
    last(messagesList.value)
  )

  const isLastMessageAssigningToAI = computed(() => {
    return (
      isGptBotPayload(lastMessage.value) &&
      lastMessage.value?.messageType ===
        MessageType.ASSIGNEE_UPDATE_NOTIFICATION
    )
  })

  const starters = computed(() => messagesStore.starters)

  const isLastAnimatedMessage = computed(() => {
    return (
      lastMessage.value && messagesStore.animateMessage === lastMessage.value.id
    )
  })

  const isAiAssigned = computed<boolean>(() => messagesStore.isAiAssigned)

  const aiQuestion = computed<Optional<string | boolean>>(
    () =>
      isAiAssigned.value &&
      (messages.value?.data?.data ?? []).find((m) =>
        isMessageSendToAi(m?.text ?? '')
      )?.text
  )
  const resetSendMessageState = () => {
    messagesStore.resetSendMessageState()
  }
  /**
   *
   * sendUserMessage docs - https://gitlab.com/qliqdev/qliqweb-vue/-/blob/dev/patient-chat/README.md?ref_type=heads#sendUserMessage-diagram
   */

  const sendUserMessage = async ({
    files,
    message: { text, attachments, messageType, payload, createdAt },
    isMessageToAIAgent,
  }: MessageCreateData) => {
    const useTypingStore = useTypingIndicatorsStore()

    if (isLoading.value) {
      return
    }

    let newText: string = text

    EventBus.emit(GlobalEvents.FocusInput, true)

    if (isMessageToAIAgent) {
      newText = `<div data-message="${AI_MESSAGE_ATTRIBUTE}" >${text}</div>`
      useTypingStore.setAiIndicatorStatus(AiTypingStatus.THINKING)
    }

    await messagesStore.sendMessage(
      {
        conversationId: conversation.value?.id ?? 0,
        text: newText,
        messageType,
        attachments,
        payload,
        createdAt,
      },
      files
    )

    EventBus.emit(GlobalEvents.ResetInputField)
  }

  // Sticky last question height calculation
  watch(aiQuestion, (newValue, oldValue) => {
    const isNewValueIsAiMessage = isMessageSendToAi(String(newValue || ''))

    if (isNewValueIsAiMessage && isUndefined(oldValue)) {
      // Additional padding for first message to AI, because question should appear in the top and need to calculate space for it
      stickyLastQuestionHeight.value = STICKY_AI_QUESTION_HEIGHT
    }
  })

  return {
    isLastMessage,
    sendUserMessage,
    messagesList,
    lastMessage,
    serverError,
    networkError,
    errorMessage,
    isLoading,
    aiQuestion,
    isAiAssigned,
    isLastAnimatedMessage,
    isAnimatedMessage,
    stickyLastQuestionHeight,
    isLastMessageAssigningToAI,
    starters,
    resetSendMessageState,
  }
}
